/**
 * Step 4: Eligibility & Conditions
 * 
 * Define eligibility criteria, conditions, usage limits, and validity
 * - Eligibility: User Type dropdown
 * - Conditions: Min order amount, min quantity, min duration, full payment checkbox
 * - Usage Limits: Total limit, per user limit
 * - Validity: Start date, end date, schedule activation (with validation)
 */

import React, { useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { PromoFormData, PromoValidationErrors, UserType } from '../../../../../api/promos/types';
import '../../../Campaigns/CreateCampaign/components/StepperButtons.css';
import '../../../Campaigns/CreateCampaign/components/StepperFormStyles.css';
import './StepEligibility.css';

interface StepEligibilityProps {
  formData: PromoFormData;
  validationErrors: PromoValidationErrors;
  onChange: (updates: Partial<PromoFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const StepEligibility: React.FC<StepEligibilityProps> = ({
  formData,
  validationErrors,
  onChange,
  onNext,
  onPrevious,
}) => {
  // Refs to track input values and prevent browser autocomplete changes
  const minOrderAmountRef = useRef<string>(String(formData.conditions?.minOrderAmount || ''));
  const minQuantityRef = useRef<string>(String(formData.conditions?.minQuantity || ''));
  const minDurationRef = useRef<string>(String(formData.conditions?.minDurationMonths || ''));
  const totalLimitRef = useRef<string>(String(formData.usageLimits?.totalLimit || ''));
  const perUserLimitRef = useRef<string>(String(formData.usageLimits?.perUserLimit || ''));

  // Update refs when formData changes
  React.useEffect(() => {
    minOrderAmountRef.current = String(formData.conditions?.minOrderAmount || '');
    minQuantityRef.current = String(formData.conditions?.minQuantity || '');
    minDurationRef.current = String(formData.conditions?.minDurationMonths || '');
    totalLimitRef.current = String(formData.usageLimits?.totalLimit || '');
    perUserLimitRef.current = String(formData.usageLimits?.perUserLimit || '');
  }, [
    formData.conditions?.minOrderAmount,
    formData.conditions?.minQuantity,
    formData.conditions?.minDurationMonths,
    formData.usageLimits?.totalLimit,
    formData.usageLimits?.perUserLimit,
  ]);
  // User Type options
  const userTypeOptions = [
    { value: UserType.ALL, label: 'All' },
    { value: UserType.NEW, label: 'New Users Only' },
    { value: UserType.RENEWAL, label: 'Renewal' },
    { value: UserType.REPEAT, label: 'Repeat' },
  ];

  // React-select styles (matching filter dropdowns)
  const getSelectStyles = () => ({
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any) => ({
      ...base,
      height: '38px',
      minHeight: '38px',
      border: '1px solid #e3e6f0',
      borderRadius: '6px',
      fontSize: '14px',
      boxShadow: 'none',
      width: '100%',
      borderColor: '#e3e6f0',
      '&:hover': {
        borderColor: '#e3e6f0'
      }
    }),
    valueContainer: (base: any) => ({
      ...base,
      height: '36px',
      padding: '0 8px',
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
  });

  // Get minimum date for end date (start date + 1 day)
  const getMinEndDate = (): string => {
    if (formData.validity?.startAt) {
      const startDate = new Date(formData.validity.startAt);
      startDate.setDate(startDate.getDate() + 1);
      return startDate.toISOString().split('T')[0];
    }
    return '';
  };

  // Handle user type change
  const handleUserTypeChange = (selectedOption: any) => {
    onChange({
      eligibility: {
        userType: selectedOption?.value || UserType.ALL,
      }
    });
  };

  // Handle conditions change
  const handleConditionsChange = (field: string, value: any) => {
    onChange({
      conditions: {
        ...formData.conditions,
        fullPaymentRequired: true, // Always true
        [field]: value,
      }
    });
  };

  // Handle usage limits change
  const handleUsageLimitsChange = (field: string, value: number | undefined) => {
    onChange({
      usageLimits: {
        ...formData.usageLimits,
        [field]: value,
      }
    });
  };

  // Handle validity change
  const handleValidityChange = (field: string, value: string | boolean) => {
    const updatedValidity = {
      ...formData.validity,
      [field]: value,
    };

    // If start date changes and end date exists, validate end date is after start date
    if (field === 'startAt' && updatedValidity.endAt && typeof value === 'string') {
      const startDate = new Date(value);
      const endDate = new Date(updatedValidity.endAt);
      if (endDate <= startDate) {
        updatedValidity.endAt = '';
      }
    }

    // If end date changes, validate it's after start date
    if (field === 'endAt' && updatedValidity.startAt && typeof value === 'string') {
      const startDate = new Date(updatedValidity.startAt);
      const endDate = new Date(value);
      if (endDate <= startDate) {
        // Don't update if end date is before or equal to start date
        return;
      }
    }

    // If schedule activation is unchecked, clear schedule date
    if (field === 'scheduleActivation' && value === false) {
      updatedValidity.scheduleDate = undefined;
    }

    onChange({
      validity: updatedValidity,
    });
  };

  return (
    <form autoComplete="off" noValidate>
      <div className="mb-4">
        <h5 className="mb-2">Eligibility & Conditions</h5>
        <p className="text-muted mb-0">Define who can use this promo and under what conditions</p>
      </div>

      <Row>
        {/* Eligibility & Conditions Section - No border */}
        <Col lg={12} className="mb-4">
          <div className="eligibility-section-no-border">
            
            <Row>
              {/* User Eligibility - Full width, single line */}
              <Col lg={12} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    User Eligibility <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    styles={getSelectStyles()}
                    options={userTypeOptions}
                    value={userTypeOptions.find(opt => opt.value === (formData.eligibility?.userType || UserType.ALL)) || null}
                    onChange={handleUserTypeChange}
                    placeholder="Select User Eligibility"
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                  {validationErrors.eligibility?.userType && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.eligibility.userType}
                    </div>
                  )}
                </Form.Group>
              </Col>
              {/* Other 3 fields - Second line */}
              <Col lg={4} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Minimum Order Value <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.conditions?.minOrderAmount ? 'is-invalid' : ''}`}
                    placeholder="Enter minimum order value"
                    value={formData.conditions?.minOrderAmount || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      minOrderAmountRef.current = val;
                      handleConditionsChange('minOrderAmount', val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      minOrderAmountRef.current = val;
                      if (val !== String(formData.conditions?.minOrderAmount || '')) {
                        handleConditionsChange('minOrderAmount', val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    step="0.01"
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  {validationErrors.conditions?.minOrderAmount && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.conditions.minOrderAmount}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={4} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">Minimum Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    className="stepper-form-control"
                    placeholder="Enter minimum quantity"
                    value={formData.conditions?.minQuantity || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      minQuantityRef.current = val;
                      handleConditionsChange('minQuantity', val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      minQuantityRef.current = val;
                      if (val !== String(formData.conditions?.minQuantity || '')) {
                        handleConditionsChange('minQuantity', val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="1"
                    step="1"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </Form.Group>
              </Col>
              <Col lg={4} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">Minimum Duration(months)</Form.Label>
                  <Form.Control
                    type="number"
                    className="stepper-form-control"
                    placeholder="Enter minimum duration"
                    value={formData.conditions?.minDurationMonths || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      minDurationRef.current = val;
                      handleConditionsChange('minDurationMonths', val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      minDurationRef.current = val;
                      if (val !== String(formData.conditions?.minDurationMonths || '')) {
                        handleConditionsChange('minDurationMonths', val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="1"
                    step="1"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </Form.Group>
              </Col>
              <Col lg={12} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <div className="eligibility-checkbox-container">
                    <Form.Check
                      type="checkbox"
                      id="fullPaymentRequired"
                      label="Require Full Payment"
                      checked={true}
                      disabled={true}
                      className="eligibility-checkbox"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>

        {/* Usage Limits Section */}
        <Col lg={12} className="mb-4">
          <div className="eligibility-section-usage-limits">
            <h6 className="eligibility-section-title mb-3">Usage Limits</h6>
            <p className="eligibility-section-subtitle mb-3">Control how many times this promo can be used</p>
            <Row>
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Total Usage Limit <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.usageLimits?.totalLimit ? 'is-invalid' : ''}`}
                    placeholder="Enter total usage limit"
                    value={formData.usageLimits?.totalLimit || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      totalLimitRef.current = val;
                      handleUsageLimitsChange('totalLimit', val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      totalLimitRef.current = val;
                      if (val !== String(formData.usageLimits?.totalLimit || '')) {
                        handleUsageLimitsChange('totalLimit', val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    step="1"
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  {validationErrors.usageLimits?.totalLimit && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.usageLimits.totalLimit}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Per User Limit <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    className={`stepper-form-control ${validationErrors.usageLimits?.perUserLimit ? 'is-invalid' : ''}`}
                    placeholder="Enter per user limit"
                    value={formData.usageLimits?.perUserLimit || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      perUserLimitRef.current = val;
                      handleUsageLimitsChange('perUserLimit', val ? Number(val) : undefined);
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      perUserLimitRef.current = val;
                      if (val !== String(formData.usageLimits?.perUserLimit || '')) {
                        handleUsageLimitsChange('perUserLimit', val ? Number(val) : undefined);
                      }
                    }}
                    onWheel={(e) => {
                      // Prevent scroll from changing number input values
                      e.currentTarget.blur();
                    }}
                    min="0"
                    step="1"
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    data-lpignore="true"
                    data-form-type="other"
                  />
                  {validationErrors.usageLimits?.perUserLimit && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.usageLimits.perUserLimit}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Valid From Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className={`stepper-form-control ${validationErrors.validity?.startAt ? 'is-invalid' : ''}`}
                    value={formData.validity?.startAt || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleValidityChange('startAt', value);
                      // If startAt is cleared, also clear endAt
                      if (!value && formData.validity?.endAt) {
                        handleValidityChange('endAt', '');
                      }
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  {validationErrors.validity?.startAt && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.validity.startAt}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <Form.Label className="stepper-form-label">
                    Expiry Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    className={`stepper-form-control ${validationErrors.validity?.endAt ? 'is-invalid' : ''}`}
                    value={formData.validity?.endAt || ''}
                    onChange={(e) => handleValidityChange('endAt', e.target.value)}
                    min={getMinEndDate() || formData.validity?.startAt || new Date().toISOString().split('T')[0]}
                    disabled={!formData.validity?.startAt}
                    required
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                  {!formData.validity?.startAt && (
                    <small className="text-muted">Please select Valid From Date first</small>
                  )}
                  {validationErrors.validity?.endAt && (
                    <div className="stepper-invalid-feedback">
                      {validationErrors.validity.endAt}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col lg={12} className="mb-3">
                <Form.Group className="stepper-form-group">
                  <div className="eligibility-checkbox-container">
                    <Form.Check
                      type="checkbox"
                      id="scheduleActivation"
                      label="Schedule Activation"
                      checked={formData.validity?.scheduleActivation || false}
                      onChange={(e) => handleValidityChange('scheduleActivation', e.target.checked)}
                      className="eligibility-checkbox"
                      disabled={true}
                    />
                  </div>
                </Form.Group>
              </Col>
              {formData.validity?.scheduleActivation && (
                <Col lg={6} className="mb-3">
                  <Form.Group className="stepper-form-group">
                    <Form.Label className="stepper-form-label">Schedule Date</Form.Label>
                    <Form.Control
                      type="date"
                      className="stepper-form-control"
                      value={formData.validity?.scheduleDate || ''}
                      onChange={(e) => handleValidityChange('scheduleDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </div>
        </Col>
      </Row>

      <div className="d-flex justify-content-between mt-4">
        <button className="stepper-back-button" onClick={onPrevious}>
          <i className="ph-arrow-left"></i>
          Back
        </button>
        <button 
          className="stepper-next-button" 
          onClick={onNext}
          disabled={
            !formData.eligibility?.userType ||
            !formData.conditions?.minOrderAmount ||
            !formData.usageLimits?.totalLimit ||
            !formData.usageLimits?.perUserLimit ||
            !formData.validity?.startAt ||
            !formData.validity?.endAt
          }
        >
          Next
          <i className="ph-arrow-right"></i>
        </button>
      </div>
    </form>
  );
};

export default StepEligibility;
